import { useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Premium product image viewer — two STRICTLY separate modes.
 *
 * Mode A — Normal inline display (`<ZoomImage />`):
 *   - Plain <img>. No pinch. No drag. No pan. No touch interception.
 *   - touch-action: pan-y so the user can scroll the page through the image.
 *   - Single tap opens the dedicated zoom viewer.
 *
 * Mode B — Dedicated fullscreen zoom viewer (`<FullscreenViewer />`):
 *   - Activated only when the user explicitly taps the inline image.
 *   - Pinch zoom + 1-finger pan when zoomed.
 *   - Movement matches finger direction on all axes (no inversion).
 *   - Close via the X button or tapping outside the image.
 */
export default function ZoomImage({ src, alt = "", className = "" }) {
  const [fsOpen, setFsOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setFsOpen(true)}
        aria-label="Open zoom viewer"
        data-testid="product-image-zoom"
        className={`relative block w-full overflow-hidden rounded-2xl bg-brand-cream border border-brand-line cursor-zoom-in ${className}`}
        // pan-y allows the browser to scroll the page vertically through the image
        // while still allowing horizontal swipes (e.g. for carousels) to fall through.
        style={{ touchAction: "pan-y" }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
        />
      </button>
      {fsOpen &&
        createPortal(
          <FullscreenViewer src={src} alt={alt} onClose={() => setFsOpen(false)} />,
          document.body,
        )}
    </>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */

function FullscreenViewer({ src, alt, onClose }) {
  // Lock body scroll while fullscreen is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Escape key closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] bg-black animate-fade-in"
      data-testid="zoom-fullscreen"
      onClick={(e) => {
        // Clicks on the backdrop close (PanZoom stops propagation on its own container)
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close zoom"
        className="absolute top-5 right-5 z-[210] w-12 h-12 rounded-full bg-white/10 backdrop-blur text-white flex items-center justify-center active:bg-white/20 [@media(hover:hover)]:hover:bg-white/20 transition-colors"
        data-testid="zoom-close-btn"
      >
        <X className="w-5 h-5" strokeWidth={1.7} />
      </button>

      <PanZoom src={src} alt={alt} onTap={onClose} />
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────────── */

/**
 * PanZoom — fullscreen-only pinch-zoom + 1-finger pan.
 *
 * Pan invariant: image follows finger 1:1. Diagonals are not split. Y is not inverted.
 *   Δtx = current_finger_x − start_finger_x
 *   Δty = current_finger_y − start_finger_y
 *
 * Pinch invariant: image scales smoothly around the midpoint between the two fingers.
 *
 * Tap: a clean single touch (no movement, no pinch) calls onTap → closes viewer.
 */
function PanZoom({ src, alt = "", onTap }) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);

  // Live touch state — never persisted in React state to avoid stale closures.
  const pointers = useRef(new Map());      // pointerId → {x, y}
  const pinchRef = useRef(null);           // {dist, scale, tx, ty, midX, midY}
  const panRef = useRef(null);             // {startX, startY, startTx, startTy}
  const tapRef = useRef({ x: 0, y: 0, t: 0, moved: false, pinched: false });

  // Wrapper size for clamping pan extent.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect?.();
      if (r) setSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const clamp = useCallback((s, x, y) => {
    const maxX = Math.max(0, ((s - 1) * size.w) / 2);
    const maxY = Math.max(0, ((s - 1) * size.h) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  }, [size.w, size.h]);

  const onPointerDown = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    try { el.setPointerCapture?.(e.pointerId); } catch { /* not supported */ }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 1) {
      tapRef.current = { x: e.clientX, y: e.clientY, t: Date.now(), moved: false, pinched: false };
      // When zoomed, prepare 1-finger pan with the LATEST committed translate.
      panRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startTx: tx,
        startTy: ty,
      };
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      pinchRef.current = {
        dist,
        scale,
        tx,
        ty,
        // midpoint in wrapper-local coordinates (relative to centre)
        midX: (a.x + b.x) / 2,
        midY: (a.y + b.y) / 2,
      };
      panRef.current = null;
      tapRef.current.pinched = true;
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Suppress tap if finger drifted more than 6px in any direction
    if (
      Math.abs(e.clientX - tapRef.current.x) > 6 ||
      Math.abs(e.clientY - tapRef.current.y) > 6
    ) {
      tapRef.current.moved = true;
    }

    if (pinchRef.current && pointers.current.size === 2) {
      // PINCH
      const rect = wrapRef.current?.getBoundingClientRect?.();
      if (!rect) return;
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      const nextScale = Math.max(1, Math.min(5, pinchRef.current.scale * (dist / pinchRef.current.dist)));

      // Anchor zoom on the original midpoint (in element-local coords).
      const cx = pinchRef.current.midX - rect.left - rect.width / 2;
      const cy = pinchRef.current.midY - rect.top - rect.height / 2;
      const dScale = nextScale - pinchRef.current.scale;
      const cl = clamp(
        nextScale,
        pinchRef.current.tx - cx * dScale,
        pinchRef.current.ty - cy * dScale,
      );
      setScale(nextScale);
      setTx(cl.x);
      setTy(cl.y);
    } else if (panRef.current && pointers.current.size === 1 && scale > 1) {
      // 1-FINGER PAN — strict 1:1 mapping, no axis swap, no inversion.
      const dx = e.clientX - panRef.current.startX;
      const dy = e.clientY - panRef.current.startY;
      const cl = clamp(scale, panRef.current.startTx + dx, panRef.current.startTy + dy);
      setTx(cl.x);
      setTy(cl.y);
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchRef.current = null;

    if (pointers.current.size === 0) {
      // If we just finished a pinch, prime panRef with current state in case the user
      // immediately starts a 1-finger drag.
      panRef.current = null;

      const wasTap =
        !tapRef.current.moved &&
        !tapRef.current.pinched &&
        Date.now() - tapRef.current.t < 350;
      if (wasTap && onTap) onTap();
    } else if (pointers.current.size === 1 && !pinchRef.current) {
      // User lifted one of two fingers after a pinch — switch to 1-finger pan mode.
      const remaining = [...pointers.current.values()][0];
      panRef.current = { startX: remaining.x, startY: remaining.y, startTx: tx, startTy: ty };
    }
  };

  return (
    <div
      ref={wrapRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onContextMenu={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
      className="absolute inset-0 overflow-hidden select-none flex items-center justify-center"
      data-testid="zoom-panzoom"
      style={{
        touchAction: "none",
        cursor: scale > 1 ? "grab" : "zoom-out",
      }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="max-w-full max-h-full object-contain will-change-transform pointer-events-none"
        style={{
          transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
          transformOrigin: "center center",
        }}
      />
    </div>
  );
}
